import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback
} from "react";
import { useHistory, useLocation } from "react-router-dom";
import IconButtonV2 from "../IconButtonV2";
import { EditorContext } from "../../editor/context/EditorContext/EditorProvider";
import { AuthContext } from "../../context/AuthProvider";
import { theme } from "../../globalStyles";
import { setPromptNode } from "../../utils/promptStore";
import useEditorAccess from "./useEditorAccess";
import EditorPopoverPanel from "./EditorPopoverPanel";
import { PopoverPanel } from "./styles";

const SEEN_KEY = "user-seen-full-editor-popover";

/*
 * Editor entry point used in the bottom play bar, track-row action strips,
 * /browse, etc. Resolves the current user's access to the track via
 * useEditorAccess, renders an IconButtonV2 with an antd Popover that
 * surfaces the richer EditorPopoverPanel. The first time an access-granted
 * user hovers the button we open the popover immediately and remember it
 * (localStorage); subsequent hovers use a slower antd delay. The popover
 * also dismisses on scroll and whenever the editor drawer opens.
 *
 * The panel content + access logic live alongside this file so other
 * surfaces (e.g. TrackActions) can present the same affordance directly
 * via IconButtonV2's popoverContent without depending on this shell.
 */
const EditorPopoverButton = ({
  track,
  size = "md",
  anchor = "bottom",
  buttonStyle = {}
}) => {
  const editor = useContext(EditorContext);
  const auth = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();
  const isGuest = auth.user.role === "guest";
  const { canAccess, onClick, tooltipText } = useEditorAccess(track);

  const promptSignup = useCallback(() => {
    setPromptNode(
      <>
        Remix any track with the{" "}
        <span style={{ color: theme.primary }}>Multitrack Editor</span> — create
        a free account to get started
      </>
    );
    history.push("/signup", { background: location });
  }, [history, location]);

  const [hasSeen, setHasSeen] = useState(
    () => !!localStorage.getItem(SEEN_KEY)
  );
  const [open, setOpen] = useState(false);

  // Auto-dismiss on scroll
  useEffect(() => {
    if (!open) return;
    const handleScroll = () => setOpen(false);
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [open]);

  // Close when the editor drawer opens
  useEffect(() => {
    if (editor.drawerState.visible) setOpen(false);
  }, [editor.drawerState.visible]);

  const handleOpenChange = nextOpen => {
    setOpen(nextOpen);
    if (nextOpen && canAccess && !hasSeen) {
      localStorage.setItem(SEEN_KEY, "true");
      setHasSeen(true);
    }
  };

  // First-time, access-granted users see the popover instantly; everyone else
  // waits ~1.5s so casual hovers don't fire it.
  const mouseEnterDelay = canAccess && !hasSeen ? 0 : 1.5;

  const placement = anchor === "top" ? "topRight" : "bottomRight";

  const popoverContent = useMemo(
    () => (
      <PopoverPanel>
        <EditorPopoverPanel canAccess={canAccess} />
      </PopoverPanel>
    ),
    [canAccess]
  );

  return (
    <IconButtonV2
      type="open_editor"
      size={size}
      disabled={isGuest ? false : !canAccess}
      onClick={isGuest ? promptSignup : onClick}
      tooltip={!open}
      tooltipText={tooltipText}
      label="Open in Editor"
      style={buttonStyle}
      popoverContent={popoverContent}
      popoverPlacement={placement}
      popoverTrigger="hover"
      popoverMouseEnterDelay={mouseEnterDelay}
      popoverMouseLeaveDelay={0.1}
      popoverOpen={open}
      onPopoverOpenChange={handleOpenChange}
      popoverInnerStyle={{
        padding: 0,
        background: "transparent",
        boxShadow: "none"
      }}
    />
  );
};

export default EditorPopoverButton;
